import { FC, useMemo } from "react";
import { Column } from "react-table";
import { Table } from "../../../../common/components/Table/Table";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { SkeletonTable } from "common/components/Table/SkeletonTable";
import { NotFoundTitle } from "common/components/Title/NotFoundTitle";
import { CustomLineChart } from "common/components/Chart/LineChart";
import { useGetAttendanceQuery } from "services/admin/adminReportsApi";
import {
  AttendanceSource,
  IAttendanceItem,
} from "common/models/admin/IReports";
import { getQueryDateParam, timeFormatter } from "common/utils/helpers";

const SOURCE_LABELS: Record<AttendanceSource, string> = {
  web: "Веб",
  app: "Приложение",
};

const sumColumn = (key: keyof IAttendanceItem) => (info: any) => (
  <>
    {info.rows.reduce(
      (sum: number, row: any) => sum + Number(row.original[key] ?? 0),
      0
    )}
  </>
);

// Счётчиков посещений сайта и звонков в системе нет, поэтому в отчёте только
// события из базы: регистрации и заказы. Источник — по push-токенам мобильных
// приложений у пользователя
export const AttendanceTable: FC<{
  source?: AttendanceSource;
  dateFrom?: string;
  dateTo?: string;
}> = ({ source, dateFrom, dateTo }) => {
  const { data, isLoading, isFetching, error } = useGetAttendanceQuery({
    source,
    date_from: getQueryDateParam(dateFrom),
    date_to: getQueryDateParam(dateTo),
  });

  // График регистраций по дням (при разбивке по источникам — суммарно)
  const chartData = useMemo(() => {
    if (!data) return [];
    const byDate = new Map<string, number>();
    data.items.forEach((item) => {
      byDate.set(item.date, (byDate.get(item.date) ?? 0) + item.registrations);
    });
    return [...byDate.entries()].map(([date, value]) => ({ date, value }));
  }, [data]);

  return (
    <QueryLayout
      isLoading={isLoading}
      error={error}
      skeleton={<SkeletonTable rowCount={6} colCount={columns.length} />}
    >
      {data?.items.length ? (
        <>
          {chartData.length > 1 && (
            <div className="mb-[20px]">
              <CustomLineChart chartData={chartData} />
            </div>
          )}
          <Table data={data.items} columns={columns} isLoading={isFetching} />
        </>
      ) : (
        <NotFoundTitle />
      )}
    </QueryLayout>
  );
};

const columns: Column<IAttendanceItem>[] = [
  {
    Header: "Дата",
    accessor: "date",
    Footer: "Итого",
    Cell: ({ row }: any) => (
      <span className="whitespace-nowrap">
        {timeFormatter({ time: new Date(row.original.date), mode: "dd-MM-yyyy" })}
      </span>
    ),
  },
  {
    Header: "Источник",
    accessor: "source",
    Cell: ({ row }: any) => (
      <>{SOURCE_LABELS[row.original.source as AttendanceSource]}</>
    ),
  },
  {
    Header: "Регистрации",
    accessor: "registrations",
    Footer: sumColumn("registrations"),
  },
  {
    Header: "Новые заказы",
    accessor: "orders",
    Footer: sumColumn("orders"),
  },
  {
    Header: "Завершённые заказы",
    accessor: "finished_orders",
    Footer: sumColumn("finished_orders"),
  },
  {
    Header: "Отменённые заказы",
    accessor: "canceled_orders",
    Footer: sumColumn("canceled_orders"),
  },
];
